/**
 * Whether the server still accepts this device's session, asked when a
 * socket cannot connect. The browser reports a refused WebSocket handshake
 * like a network failure (1006), so a device whose session ended while its
 * sockets were down (an expired cookie, a revoked session) used to retry for
 * hours while every change stayed on it: found on production on 2026-10-13.
 * Now a failed connection asks GET /api/auth/me (at most one question a
 * minute); a 401 announces the session ended (Events.SESSION_ENDED: the
 * sign-in dialog of interfaces/classic/ui/session-resume.js, changes kept, the server indicator
 * says so). A network error or any other answer says nothing. A socket the
 * server closes because the session ended (4401) announces it here too: one
 * announcement until a socket connects again.
 */
import { emit, Events } from "../core/events.js";

// Bound (not timing): how often a device that cannot connect asks.
const ASK_EVERY_MS = 60000;

let lastAsked = -Infinity;
let asking = null;
let announced = false;

/**
 * Ask the server whether it accepts this device's session.
 * @returns {Promise<'accepted'|'refused'|'unknown'>}
 */
export async function askSession(fetchImpl = globalThis.fetch) {
  try {
    const response = await fetchImpl("/api/auth/me", {
      credentials: "include",
      cache: "no-store",
      headers: { "X-Requested-With": "XMLHttpRequest" },
    });
    if (response.status === 401) return "refused";
    return response.ok ? "accepted" : "unknown";
  } catch {
    return "unknown"; // offline, server down: the sockets' own retries cover it
  }
}

/**
 * A socket could not connect (closed before it opened, or refused): ask
 * whether the session is the reason, at most once a minute.
 * @returns {Promise<'accepted'|'refused'|'unknown'|'skipped'>}
 */
export function noteConnectFailed({ now = Date.now(), fetchImpl } = {}) {
  if (announced || asking || now - lastAsked < ASK_EVERY_MS) return Promise.resolve("skipped");
  lastAsked = now;
  asking = askSession(fetchImpl).then((answer) => {
    if (answer === "refused") announceSessionEnded("session_refused");
    return answer;
  }).finally(() => { asking = null; });
  return asking;
}

/** This device's session ended (a 4401 close, or refused): say so once. */
export function announceSessionEnded(reason) {
  if (announced) return;
  announced = true;
  console.warn(`[Session] This device's session ended (${reason}): changes stay here until signing in again`);
  emit(Events.SESSION_ENDED, { reason });
}

/** A socket connected, or the user signed in: a later refusal is announced again. */
export function noteConnected() {
  announced = false;
  lastAsked = -Infinity;
}
